import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Boxes, CornerDownLeft, Search, User } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { assetsApi, employeesApi } from '@/api/endpoints';
import { routeInLicensedModule } from './moduleAccess';
import { NAVIGATION } from './navigation';
import { AssetStatusBadge } from '@/design-system/DataDisplay';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Command palette and global search.
 *
 * Search hits the same paged, server-side endpoints the list screens use - there is no separate
 * search index, and nothing is filtered in the browser. Results are limited to a handful because
 * the palette is for jumping somewhere, not for browsing.
 *
 * Everything offered respects permissions: a user without EMPLOYEE_READ never sees people here,
 * and the query is not even issued.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { can, canAny, hasModule } = useAuth();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const canReadAssets = can(PERMISSIONS.ASSET_READ);
  const canReadEmployees = can(PERMISSIONS.EMPLOYEE_READ);
  const hasQuery = debouncedSearch.trim().length >= 2;

  const { data: assets, isFetching: assetsLoading } = useQuery({
    queryKey: ['palette', 'assets', debouncedSearch],
    queryFn: () => assetsApi.search({ search: debouncedSearch, size: 5 }),
    enabled: open && canReadAssets && hasQuery,
  });

  const { data: employees, isFetching: employeesLoading } = useQuery({
    queryKey: ['palette', 'employees', debouncedSearch],
    queryFn: () => employeesApi.search({ search: debouncedSearch, size: 5 }),
    enabled: open && canReadEmployees && hasQuery,
  });

  if (!open) return null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const navItems = NAVIGATION.flatMap((section) => section.items).filter(
    (item) => canAny(item.anyOf) && routeInLicensedModule(item.to, hasModule),
  );

  return (
    <div className="fixed inset-0 z-100 flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-overlay backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <Command
        label="Command palette"
        loop
        className="relative w-full max-w-xl overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-xl animate-in-scale"
      >
        <div className="flex items-center gap-2.5 border-b border-border-subtle px-4">
          <Search aria-hidden className="size-4 shrink-0 text-text-muted" />
          <Command.Input
            autoFocus
            value={search}
            onValueChange={setSearch}
            placeholder="Search assets and people, or jump to a section…"
            className="h-12 flex-1 bg-transparent text-body text-text-primary outline-none placeholder:text-text-muted"
          />
          <kbd className="rounded border border-border-default px-1.5 py-0.5 font-mono text-[0.6875rem] text-text-muted">
            Esc
          </kbd>
        </div>

        <Command.List className="max-h-[22rem] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-8 text-center text-small text-text-muted">
            {hasQuery
              ? assetsLoading || employeesLoading
                ? 'Searching…'
                : 'Nothing matched that search.'
              : 'Type at least two characters to search.'}
          </Command.Empty>

          <Command.Group
            heading={<GroupHeading label="Go to" />}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1"
          >
            {navItems.map((item) => (
              <PaletteItem
                key={item.to}
                value={`goto ${item.label} ${item.description}`}
                onSelect={() => go(item.to)}
                icon={<item.icon className="size-4" />}
                title={item.label}
                subtitle={item.description}
              />
            ))}
          </Command.Group>

          {canReadAssets && !!assets?.content.length && (
            <Command.Group
              heading={<GroupHeading label="Assets" />}
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1"
            >
              {assets.content.map((asset) => (
                <PaletteItem
                  key={asset.id}
                  value={`asset ${asset.assetTag ?? ''} ${asset.serialNumber ?? ''} ${asset.id}`}
                  onSelect={() => go(`/assets/${asset.id}`)}
                  icon={<Boxes className="size-4" />}
                  title={asset.assetTag ?? asset.serialNumber ?? 'Untagged asset'}
                  subtitle={asset.serialNumber ?? undefined}
                  trailing={<AssetStatusBadge status={asset.status} />}
                />
              ))}
            </Command.Group>
          )}

          {canReadEmployees && !!employees?.content.length && (
            <Command.Group
              heading={<GroupHeading label="People" />}
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1"
            >
              {employees.content.map((employee) => (
                <PaletteItem
                  key={employee.id}
                  value={`employee ${employee.fullName} ${employee.email} ${employee.employeeNumber}`}
                  onSelect={() => go(`/employees/${employee.id}`)}
                  icon={<User className="size-4" />}
                  title={employee.fullName ?? ''}
                  subtitle={`${employee.employeeNumber ?? ''} · ${employee.email ?? ''}`}
                />
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command>
    </div>
  );
}

function GroupHeading({ label }: { label: string }) {
  return <span className="text-label text-text-muted">{label}</span>;
}

function PaletteItem({
  value,
  onSelect,
  icon,
  title,
  subtitle,
  trailing,
}: {
  value: string;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-small text-text-secondary data-[selected=true]:bg-brand-subtle data-[selected=true]:text-brand-text"
    >
      <span aria-hidden className="shrink-0 text-text-muted">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-text-primary">{title}</span>
        {subtitle && <span className="block truncate text-caption text-text-muted">{subtitle}</span>}
      </span>
      {trailing}
      <CornerDownLeft aria-hidden className="size-3.5 shrink-0 opacity-0 data-[selected=true]:opacity-60" />
    </Command.Item>
  );
}
